import { useEffect, useSyncExternalStore } from "react";
import { fetchClubLogos } from "./clubApi";

// null means the club has no logo; a missing key means it hasn't been fetched yet.
let logos: Record<number, string | null> = {};
const requested = new Set<number>();
const listeners = new Set<() => void>();

function emit(next: Record<number, string | null>) {
  logos = next;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function setCachedClubLogo(clubId: number, logo: string | null) {
  requested.add(clubId);
  emit({ ...logos, [clubId]: logo });
}

export function resetClubLogos() {
  requested.clear();
  emit({});
}

function useClubLogos(devPort: string, clubIds: number[]): Record<number, string | null> {
  const snapshot = useSyncExternalStore(subscribe, () => logos);
  const idsKey = clubIds.join(",");

  useEffect(() => {
    const missing = idsKey
      .split(",")
      .map(Number)
      .filter((clubId) => clubId > 0 && !requested.has(clubId));
    if (missing.length === 0) {
      return;
    }
    missing.forEach((clubId) => requested.add(clubId));
    fetchClubLogos(devPort, missing)
      .then((fetched) => {
        const next = { ...logos };
        missing.forEach((clubId) => {
          next[clubId] = fetched[clubId] ?? null;
        });
        emit(next);
      })
      .catch(() => missing.forEach((clubId) => requested.delete(clubId)));
  }, [devPort, idsKey]);

  return snapshot;
}

export default useClubLogos;
